import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  InputAdornment,
  MenuItem,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { useMutation } from '@apollo/client';
import { useController, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ADD_SITE_COLLABORATOR_MUTATION,
  GET_SITE_COLLABORATORS_QUERY,
  REMOVE_SITE_COLLABORATOR_MUTATION,
  UPDATE_SITE_COLLABORATOR_LEVEL_MUTATION,
} from '../../../gql';
import { formatDate } from '../../../helpers';
import {
  Button,
  IconButton,
  Pill,
  PillSelect,
  PillSelectOption,
  SurfaceAutocomplete,
  SurfaceSelect,
  SurfaceTextField,
} from '../../../components/design-system';

interface Person {
  id: string;
  displayName?: string | null;
  email: string;
  createdAt: string;
  level: number;
  userId: string;
}

interface Contact {
  id: string;
  displayName?: string | null;
  email: string;
  responderUser: string;
}

interface AccessGridProps {
  data: Person[];
  contacts: Contact[];
  siteId?: string;
}

interface CollaboratorRowProps {
  levelLabel: (value: number) => string;
  person: Person;
  removing: boolean;
  updating: boolean;
  removeLabel: string;
  levelOptions: PillSelectOption[];
  selectedLevel: number;
  onLevelChange: (person: Person, level: number) => void;
  onRemove: (person: Person) => void;
}

interface AccessFormValues {
  collaborator: Contact | null;
  level: number;
}

type SortKey = 'displayName' | 'email' | 'level' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const levels = [0, 1, 2];
const defaultValues: AccessFormValues = { collaborator: null, level: 1 };
const cardStyles = {
  p: { xs: 1.5, sm: 2 },
  border: '1px solid',
  borderColor: 'softBorder',
  backgroundImage: 'none',
};
const addFormStyles = {
  gap: 2,
  alignItems: { sm: 'center' },
  flexWrap: 'wrap',
};
const collaboratorFieldStyles = {
  width: { xs: '100%', sm: 350 },
};
const levelSelectStyles = {
  width: { xs: '100%', sm: 190 },
};
const addButtonStyles = { minHeight: 42, px: 2.5 };
const helpStyles = {
  mt: 2,
  p: 1.5,
  border: '1px solid',
  borderColor: 'softBorder',
  borderRadius: 1,
  bgcolor: 'action.hover',
  alignItems: 'center',
};
const legendStyles = { flexWrap: 'wrap', flexShrink: 0 };
const helpTextStyles = { color: 'text.secondary', lineHeight: 1.35 };
const tableContainerStyles = {
  mt: 2,
  border: '1px solid',
  borderColor: 'softBorder',
  borderRadius: 1,
};
const tableStyles = {
  minWidth: 780,
  '& .MuiTableCell-root': { borderColor: 'softBorder' },
  '& .MuiTableCell-head': { fontWeight: 650 },
};
const personCellStyles = { alignItems: 'center' };
const avatarStyles = {
  width: 30,
  height: 30,
  fontSize: '0.75rem',
  bgcolor: 'action.selected',
  color: 'text.primary',
};
const footerStyles = { color: 'text.secondary', mt: 1.5, ml: 1 };
const contactOptionStyles = { minHeight: 42, alignItems: 'center' };
const contactLabelStyles = {
  display: 'flex',
  minWidth: 0,
  alignItems: 'baseline',
  gap: 0.75,
};
const contactNameStyles = { flexShrink: 0 };
const contactEmailStyles = { color: 'text.secondary' };

const getInitials = (person: Pick<Person, 'displayName' | 'email'>) => {
  const source = person.displayName?.trim() || person.email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
};

const compareValues = (left: unknown, right: unknown) =>
  String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

const CollaboratorRow = React.memo(
  ({
    levelLabel,
    person,
    removing,
    updating,
    removeLabel,
    levelOptions,
    selectedLevel,
    onLevelChange,
    onRemove,
  }: CollaboratorRowProps) => {
    const handleRemove = useCallback(() => onRemove(person), [
      onRemove,
      person,
    ]);
    const handleLevelChange = useCallback(
      (event: SelectChangeEvent<number | string>) => {
        onLevelChange(person, Number(event.target.value));
      },
      [onLevelChange, person]
    );

    return (
      <TableRow hover>
        <TableCell>
          <Stack direction="row" gap={1.25} sx={personCellStyles}>
            <Avatar sx={avatarStyles}>{getInitials(person)}</Avatar>
            <Typography>{person.displayName || '—'}</Typography>
          </Stack>
        </TableCell>
        <TableCell>{person.email}</TableCell>
        <TableCell>
          <PillSelect
            disabled={updating}
            inputProps={{ 'aria-label': levelLabel(selectedLevel) }}
            options={levelOptions}
            value={selectedLevel}
            onChange={handleLevelChange}
          />
        </TableCell>
        <TableCell>{formatDate(new Date(person.createdAt))}</TableCell>
        <TableCell align="center">
          <IconButton
            disabled={removing}
            aria-label={removeLabel}
            density="compact"
            onClick={handleRemove}
            tone="danger"
            variant="ghost"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }
);

const AccessGrid = ({ data, contacts, siteId }: AccessGridProps) => {
  const intl = useIntl();
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [pendingLevels, setPendingLevels] = useState<Record<string, number>>(
    {}
  );
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const { control, handleSubmit, reset } = useForm<AccessFormValues>({
    defaultValues,
  });
  const collaborator = useController({ name: 'collaborator', control });
  const level = useController({ name: 'level', control });
  const selectedContact = useWatch({ control, name: 'collaborator' });
  const activeCollaborators = useMemo(
    () => new Set(data.map((person) => person.userId)),
    [data]
  );
  const availableContacts = useMemo(
    () =>
      contacts.filter(
        (contact) => !activeCollaborators.has(contact.responderUser)
      ),
    [activeCollaborators, contacts]
  );
  const sortedPeople = useMemo(() => {
    const direction = sortDirection === 'asc' ? 1 : -1;

    return [...data].sort(
      (left, right) => compareValues(left[sortKey], right[sortKey]) * direction
    );
  }, [data, sortDirection, sortKey]);
  const refetchQueries = useMemo(
    () =>
      siteId
        ? [
            {
              query: GET_SITE_COLLABORATORS_QUERY,
              variables: { siteId, pagination: { limit: 1000 } },
            },
          ]
        : [],
    [siteId]
  );
  const [
    addCollaborator,
    { loading: adding },
  ] = useMutation(ADD_SITE_COLLABORATOR_MUTATION, { refetchQueries });
  const [
    removeCollaborator,
    { loading: removing },
  ] = useMutation(REMOVE_SITE_COLLABORATOR_MUTATION, { refetchQueries });
  const [
    updateCollaboratorLevel,
  ] = useMutation(UPDATE_SITE_COLLABORATOR_LEVEL_MUTATION, { refetchQueries });

  const levelLabel = useCallback(
    (value: number) => {
      const labels = [
        intl.formatMessage({
          id: 'access.level.read',
          defaultMessage: 'Read',
        }),
        intl.formatMessage({
          id: 'access.level.add',
          defaultMessage: 'Add',
        }),
        intl.formatMessage({
          id: 'access.level.edit',
          defaultMessage: 'Edit',
        }),
      ];

      return `${value} · ${labels[value] || value}`;
    },
    [intl]
  );
  const levelOptions = useMemo<PillSelectOption[]>(
    () => [
      { value: 0, label: levelLabel(0), tone: 'neutral' },
      { value: 1, label: levelLabel(1), tone: 'info' },
      { value: 2, label: levelLabel(2), tone: 'success' },
    ],
    [levelLabel]
  );
  const getContactLabel = useCallback(
    (contact: Contact) =>
      contact.displayName
        ? `${contact.displayName} • ${contact.email}`
        : contact.email,
    []
  );
  const isOptionEqualToValue = useCallback(
    (option: Contact, value: Contact) => option.id === value.id,
    []
  );
  const renderContactOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: Contact) => (
      <Box component="li" {...props}>
        <Stack direction="row" gap={1} sx={contactOptionStyles}>
          <Avatar sx={avatarStyles}>{getInitials(option)}</Avatar>
          <Box sx={contactLabelStyles}>
            {option.displayName && (
              <Typography variant="body2" sx={contactNameStyles} noWrap>
                {option.displayName}
              </Typography>
            )}
            <Typography variant="body2" sx={contactEmailStyles} noWrap>
              {option.displayName && '• '}
              {option.email}
            </Typography>
          </Box>
        </Stack>
      </Box>
    ),
    []
  );
  const handleContactChange = useCallback(
    (_event: SyntheticEvent, value: Contact | null) => {
      collaborator.field.onChange(value);
    },
    [collaborator.field]
  );
  const handleLevelChange = useCallback(
    (event: SelectChangeEvent<number>) =>
      level.field.onChange(Number(event.target.value)),
    [level.field]
  );
  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
        return;
      }

      setSortKey(key);
      setSortDirection('asc');
    },
    [sortKey]
  );
  const handleSortName = useCallback(() => handleSort('displayName'), [
    handleSort,
  ]);
  const handleSortEmail = useCallback(() => handleSort('email'), [handleSort]);
  const handleSortLevel = useCallback(() => handleSort('level'), [handleSort]);
  const handleSortDate = useCallback(() => handleSort('createdAt'), [
    handleSort,
  ]);
  const submit = useCallback(
    async (values: AccessFormValues) => {
      if (!siteId || !values.collaborator) {
        return;
      }

      await addCollaborator({
        variables: {
          siteId,
          userId: values.collaborator.responderUser,
          level: values.level,
        },
      });
      reset({ collaborator: null, level: values.level });
    },
    [addCollaborator, reset, siteId]
  );
  const remove = useCallback(
    async (person: Person) => {
      if (
        !siteId ||
        !window.confirm(
          intl.formatMessage(
            {
              id: 'site.access.confirmRemove',
              defaultMessage:
                'Are you sure you want to stop sharing this site with {email}?',
            },
            { email: person.email }
          )
        )
      ) {
        return;
      }

      await removeCollaborator({
        variables: { siteId, userId: person.userId },
      });
    },
    [intl, removeCollaborator, siteId]
  );
  const updateLevel = useCallback(
    async (person: Person, nextLevel: number) => {
      if (!siteId) {
        return;
      }

      setPendingLevels((current) => ({
        ...current,
        [person.userId]: nextLevel,
      }));
      setUpdatingUserId(person.userId);

      try {
        await updateCollaboratorLevel({
          variables: { siteId, userId: person.userId, level: nextLevel },
        });
      } catch (error) {
        setPendingLevels((current) => {
          const next = { ...current };
          delete next[person.userId];
          return next;
        });
      } finally {
        setUpdatingUserId(null);
      }
    },
    [siteId, updateCollaboratorLevel]
  );
  const removeLabel = intl.formatMessage({
    id: 'access.removeCollaborator',
    defaultMessage: 'Remove collaborator',
  });

  return (
    <Paper elevation={0} sx={cardStyles}>
      <Stack
        component="form"
        direction={{ xs: 'column', sm: 'row' }}
        onSubmit={handleSubmit(submit)}
        sx={addFormStyles}
      >
        <SurfaceAutocomplete
          disablePortal
          id="site-collaborator-user"
          options={availableContacts}
          value={collaborator.field.value}
          disabled={availableContacts.length === 0}
          isOptionEqualToValue={isOptionEqualToValue}
          getOptionLabel={getContactLabel}
          renderOption={renderContactOption}
          onChange={handleContactChange}
          surface="sunken"
          renderInput={(params) => (
            <SurfaceTextField
              {...params}
              inputRef={collaborator.field.ref}
              size="small"
              placeholder={intl.formatMessage({
                id: 'access.selectContact',
                defaultMessage: 'Select a contact',
              })}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <PersonAddAltOutlinedIcon color="action" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              surface="sunken"
            />
          )}
          sx={collaboratorFieldStyles}
        />
        <SurfaceSelect
          id="site-collaborator-level"
          name={level.field.name}
          value={level.field.value}
          inputRef={level.field.ref}
          disabled={availableContacts.length === 0}
          onBlur={level.field.onBlur}
          onChange={handleLevelChange}
          size="small"
          sx={levelSelectStyles}
          surface="sunken"
        >
          {levels.map((value) => (
            <MenuItem key={value} value={value}>
              {levelLabel(value)}
            </MenuItem>
          ))}
        </SurfaceSelect>
        <Button
          disabled={adding || !selectedContact}
          type="submit"
          startIcon={<AddIcon />}
          sx={addButtonStyles}
        >
          <FormattedMessage
            id="access.addCollaborator"
            defaultMessage="Add collaborator"
          />
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={helpStyles}>
        <Stack direction="row" gap={1} sx={legendStyles}>
          {levelOptions.map((option) => (
            <Pill key={option.value} tone={option.tone}>
              {option.label}
            </Pill>
          ))}
        </Stack>
        <Typography variant="body2" sx={helpTextStyles}>
          <FormattedMessage
            id="site.access.levelHelp"
            defaultMessage="Any contact added will be able to see all the site's finds. Level 0 is read-only, Level 1 can add finds, and Level 2 can edit all the site's finds."
          />
        </Typography>
      </Stack>

      <TableContainer sx={tableContainerStyles}>
        <Table size="small" sx={tableStyles}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'displayName'}
                  direction={sortKey === 'displayName' ? sortDirection : 'asc'}
                  onClick={handleSortName}
                >
                  <FormattedMessage
                    id="access.personName"
                    defaultMessage="Name"
                  />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'email'}
                  direction={sortKey === 'email' ? sortDirection : 'asc'}
                  onClick={handleSortEmail}
                >
                  E-mail
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'level'}
                  direction={sortKey === 'level' ? sortDirection : 'asc'}
                  onClick={handleSortLevel}
                >
                  <FormattedMessage id="access.level" defaultMessage="Level" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortKey === 'createdAt'}
                  direction={sortKey === 'createdAt' ? sortDirection : 'asc'}
                  onClick={handleSortDate}
                >
                  <FormattedMessage
                    id="access.addedOn"
                    defaultMessage="Added on"
                  />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <FormattedMessage
                  id="common.actions"
                  defaultMessage="Actions"
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPeople.map((person) => (
              <CollaboratorRow
                key={person.id}
                person={person}
                removing={removing}
                updating={updatingUserId === person.userId}
                levelLabel={levelLabel}
                levelOptions={levelOptions}
                selectedLevel={pendingLevels[person.userId] ?? person.level}
                removeLabel={removeLabel}
                onLevelChange={updateLevel}
                onRemove={remove}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body2" sx={footerStyles}>
        <FormattedMessage
          id="site.access.count"
          defaultMessage="{count, plural, one {# collaborator} other {# collaborators}}"
          values={{ count: data.length }}
        />
      </Typography>
    </Paper>
  );
};

export default AccessGrid;
